import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: MenuItem[];
}

const PlusButtonMenu = ({ anchorEl, open, onClose, items }: Props) => {
  const { theme } = useAITTheme();
  const menuRef = useRef<HTMLDivElement>(null);
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && anchorEl && !anchorEl.contains(target)) {
        onClose();
      }
    },
    [anchorEl, onClose]
  );

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, handleClickOutside, handleEscape]);

  if (!open || !anchorEl) return null;

  const anchorRect = anchorEl.getBoundingClientRect();
  const menuStyle: React.CSSProperties = {
    ...styles.menu,
    position: 'fixed',
    left: anchorRect.left,
    top: anchorRect.bottom + 8
  };

  return (
    <div ref={menuRef} style={menuStyle} role="menu">
      {items.map(item => (
        <button
          key={item.id}
          type="button"
          style={styles.menuItem}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          role="menuitem"
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = theme.contentBgColor;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          }}
        >
          <span style={styles.menuItemIcon}>{item.icon}</span>
          <span style={styles.menuItemLabel}>{item.label}</span>
        </button>
      ))}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  menu: {
    backgroundColor: '#ffffff',
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '12px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    padding: '8px 0',
    minWidth: '200px',
    zIndex: 1000
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    textAlign: 'left' as const,
    transition: 'background-color 0.15s ease'
  },
  menuItemIcon: {
    fontSize: '16px',
    width: '20px',
    textAlign: 'center' as const
  },
  menuItemLabel: {
    flex: 1
  }
});

export default PlusButtonMenu;
